<template>
  <div ref="chart-container" style="padding: 20px 12px 12px 12px; height: 350px; background-color: black;" />
</template>

<script>
import _ from 'lodash';
import qs from 'querystring';
import Highcharts from 'highcharts';
import { mapState, mapGetters } from 'vuex';

import SL from 'highcharts/modules/series-label';
SL(Highcharts);

import { coinDesc } from '@/api/const';

export default {
  name: 'SummaryChart',
  props: ['summary', 'coin'],
  computed: {
    ...mapState(['contracts', 'blockchain']),
    ...mapGetters('contracts', ['getContractById', 'findContracts'])
  },
  methods: {
    jump(coin, duration) {
      location.href = "#/products?" + qs.encode({ coin, duration });
    },
    draw() {
      const { blockchain, coin } = this;
      const nicehash = this.getContractById('NICEHASH-STANDARD-' + coinDesc[coin].algorithm);
      const futures = this.findContracts('DIFFICULTY_FUTURES', coin);
      if (!nicehash || (coin === 'BTC' && !futures)) return ;

      const miningPayoff = blockchain[coin].miningEarning * blockchain[coin].price * coinDesc[coin].unitFactor;
      const maxDuration = Math.max(...this.summary.contracts.map(c => c.duration));

      Highcharts.chart(this.$refs['chart-container'], {
        title: {
          text: `${this.coin} Mining Contracts Price over Maturities`,
          style: {
            fontSize: 15,
            color: 'white'
          }
        },

        credits: {
          enabled: true,
          href: 'https://honeylemon.market/',
          text: 'honeylemon.market'
        },

        chart: {
          backgroundColor: '#000',
          style: {
            fontFamily: '"Roboto", Helvetica, Arial, sans-serif'
          }
        },

        colors: ['#cece4b', '#2b908f'],

        yAxis: [{
          title: {
            text: `Best Price ($/${coinDesc[coin].unit}/Day)`,
            style: { color: 'white' }
          },
          labels: {
            style: { color: '#ccc' }
          },
          gridLineWidth: 0,
          minorGridLineWidth: 0,
          min: 0,
        }],

        xAxis: {
          type: 'datetime',
          title: {
            text: 'Expiration Date',
            style: { color: 'white' }
          },
          labels: {
            style: { color: '#ccc' }
          },
          dateTimeLabelFormats: {
            month: '%b %Y',
            year: '%b %Y'
          },
          plotBands: Date.now() < blockchain[coin].halvingTs && [{
            from: 0,
            to: blockchain[coin].halvingTs,
            color: 'rgba(255, 255, 0, 0.2)',
            width: 1,
            label: {
              text: 'Pre-halving<div style="display: inline-block; position: absolute; left: 130px;">Post-halving</div>',
              y: -10,
              useHTML: true,
              verticalAlign: 'bottom',
              style: {
                color: 'white'
              }
            }
          }],
          // tickInterval: 365 * 86400 * 1000,
          gridLineWidth: 0,
          tickWidth: 0,
          endOnTick: false
        },

        legend: {
          enabled: false
        },

        plotOptions: {
          spline: {
            states: {
              inactive: {
                opacity: 1
              }
            }
          },
          line: {
            states: {
              inactive: {
                opacity: 1
              }
            },
            dataLabels: {
              enabled: true,
              formatter: function () {
                return this.point.desc + '<br/>' + Highcharts.numberFormat(this.y,4);
              },
              style: { color: '#cece4b' }
            },
            enableMouseTracking: true
          }
        },

        tooltip: {
          useHTML: true,
          style: {
            pointerEvents: 'auto'
          },
          formatter() {
            return `
              Duration: ${this.point.desc}<br/>
              Best Price: $${this.y.toFixed(4)}/${coinDesc[coin].unit}/Day<br/>
              ${this.point.platforms}
            ` + (this.point.duration ? `<a href="#/products?coin=${coin}&duration=${this.point.duration}" style="display: block; text-align: center;">Details</a>` : '');
          }
        },

        series: [{
          name: '',
          type: 'line',
          yAxis: 0,
          tooltip: {
            valueDecimals: 4,
            valueSuffix: `$/${this.summary.unit}/Day`
          },
          data: [
            { x: Date.now(), y: nicehash.priceUsd * coinDesc[coin].unitFactor, desc: 'NiceHash', platforms: '' },
            ...this.summary.contracts.filter(c => c.duration <= 730).map(c => ({
              x: Date.now() + c.duration * 1000 * 86400,
              y: c.daily.preHalving.cost * coinDesc[coin].unitFactor,
              desc: c.durationAlias,
              duration: c.duration,
              platforms: 'Platforms: ' + this.summary.durationIssuers.get(c.durationAlias).size
            }))
          ]
        }, ...(futures ? [{
          name: '',
          type: 'line',
          yAxis: 0,
          dataLabels: { style: { color: 'white' } },
          tooltip: {
            valueDecimals: 4,
            valueSuffix: `$/${this.summary.unit}/Day`
          },
          data: futures.map(c => ({
              x: c.expiry,
              y: c.priceUsd,
              desc: 'FTX ' + /\d+ Q\d/.exec(c.name)[0],
              duration: 'FTX ' + /\d+ Q\d/.exec(c.name)[0],
              platforms: ''
            }))
        }] : []), {
          name: 'Avg daily block rewards<br/>(assume constant price & difficulty)',
          type: 'spline',
          yAxis: 0,
          color: 'white',
          enableMouseTracking: false,
          dataLabels: { enabled: false },
          marker: {
            enabled: false
          },
          dashStyle: 'dash',
          data: [
            [Date.now(), miningPayoff],
            ...(blockchain[coin].halvingTs > Date.now()) ?
              _
                .range(blockchain[coin].halvingTs, Date.now() + Math.min(maxDuration, 730) * 1000 * 86400, 1000 * 86400)
                .map(ts => [
                  ts,
                  miningPayoff * (blockchain[coin].halvingTs - Date.now() + (ts - blockchain[coin].halvingTs) / 2) / (ts - Date.now())
                ])
              : [
                [Date.now() + 730 * 1000 * 86400, miningPayoff]
              ]
          ]
        }]
      });
    }
  },
  watch: {
    ['summary.contracts']() {
      this.draw();
    },
    ['contracts.data']() {
      this.draw();
    }
  },
  mounted() {
    this.draw();
  }
}
</script>

<style scoped>

</style>